/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V100 - Lazy Load Firestore) ---
 * - MISSION: To reduce initial bundle size by lazy-loading Firestore.
 * - CHANGES:
 *   1. Removed static imports of `firebase/firestore`.
 *   2. Dynamically import `firebase/firestore` and `getDb()` only when valid user is found.
 *   3. All Firestore interactions (doc, getDoc, setDoc) are now inside async functions
 *      where the SDK is dynamically imported first.
 * 
 * --- REWRITE SUMMARY (V200 - Lazy Load Auth) ---
 * - MISSION: To further reduce initial bundle size by lazy-loading Firebase Auth interactions.
 * - CHANGES:
 *   1. Removed static imports of `firebase/auth` methods (signIn, signOut, etc.).
 *   2. Dynamically import these methods only when needed (e.g., when user clicks login).
 *   3. `onAuthStateChanged` is also imported dynamically in useEffect to defer the heavy auth logic.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import Cookies from 'js-cookie';
// import { auth } from '@/services/firebase';
import { getLazyAuth } from '@/services/firebase'; // Lazy loader
import { getDb } from '@/services/firestore'; // Lazy loader
import { UserContext } from './user.context.js';
import { getUserPassStatus, deleteUserAccount as apiDeleteUserAccount, sendWelcomeEmail, getAffiliateMe } from '@/services/api';
import { useGuestUser } from '@/hooks/useGuestUser';
import { useTranslation } from 'react-i18next';

export const UserProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [authInstance, setAuthInstance] = useState(null); // Auth instance state
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isClaimsReady, setIsClaimsReady] = useState(false);
  const [passStatus, setPassStatus] = useState('unknown'); // 'unknown', 'active', 'inactive'
  const { setGuestEmail } = useGuestUser();

  useEffect(() => {
    let unsubscribe = null;

    const initAuthListener = async () => {
      const auth = await getLazyAuth(); // Initialize Auth
      setAuthInstance(auth);
      const { onAuthStateChanged, signOut } = await import('firebase/auth');

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setIsClaimsReady(false);
        setPassStatus('unknown');

        if (firebaseUser) {
          try {
            // 1. Immediately get the latest token results.
            const idTokenResult = await firebaseUser.getIdTokenResult(true);
            const userRole = idTokenResult.claims.role || 'viewer';

            // --- LAZY LOAD FIRESTORE ---
            const db = await getDb();
            const { doc, getDoc, setDoc, serverTimestamp } = await import('firebase/firestore');
            // ---------------------------

            const userRef = doc(db, 'users', firebaseUser.uid);
            const userSnap = await getDoc(userRef);

            // 3. Construct the base user data object.
            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              providerData: firebaseUser.providerData,
              role: userRole,
              getIdToken: (forceRefresh) => firebaseUser.getIdToken(forceRefresh),
            };

            // 4. Check Firestore for an existing profile and create one if it's missing.
            if (userSnap.exists()) {
              setUser({ ...userData, ...userSnap.data(), role: userRole });
            } else {
              const emailUsername = firebaseUser.email.split('@')[0];
              const defaultDisplayName = emailUsername.charAt(0).toUpperCase() + emailUsername.slice(1);

              const newUserPayload = {
                ...userData,
                displayName: defaultDisplayName,
                referredByCode: Cookies.get('indikin_ref') || null,
                createdAt: serverTimestamp()
              };
              delete newUserPayload.getIdToken; // Don't store functions in Firestore.

              await setDoc(userRef, newUserPayload);

              try {
                localStorage.setItem('indikin_account_created', 'true');
                const freshToken = await firebaseUser.getIdToken();
                await sendWelcomeEmail(freshToken);
                setGuestEmail('');
              } catch (error) {
                console.error("Failed to send welcome email for new user:", error);
              }
              setUser({ ...userData, displayName: defaultDisplayName });
            }

            setLoading(false);
            setIsAuthReady(true);
            setIsClaimsReady(true);

            // Cache ambassador status for exit-intent suppression (fire-and-forget, non-blocking)
            // [v7 FIX] All sessionStorage calls wrapped in try/catch for browsers with storage disabled
            firebaseUser.getIdToken().then(idToken => {
              getAffiliateMe(idToken).then(affiliateData => {
                try {
                  if (affiliateData?.code) {
                    sessionStorage.setItem('indikin_is_ambassador', 'true');
                  } else {
                    sessionStorage.removeItem('indikin_is_ambassador');
                  }
                } catch { /* Storage unavailable - non-critical */ }
              }).catch(() => {
                try { sessionStorage.removeItem('indikin_is_ambassador'); } catch { /* noop */ }
              });
            }).catch(() => { /* Token fetch failed - non-critical */ });

            if (userRole === 'admin' || userRole === 'filmmaker') {
              setPassStatus('active');
            }

          } catch (error) {
            console.error("Error during user verification:", error);
            const auth = await getLazyAuth();
            await signOut(auth);
            setUser(null);
            setLoading(false);
            setIsAuthReady(true);
            setIsClaimsReady(true);
          }
        } else {
          // No Firebase user found.
          setUser(null);
          setLoading(false);
          setIsAuthReady(true);
          setIsClaimsReady(true);
          setPassStatus('inactive');
          // [v7 FIX] try/catch for browsers with storage disabled
          try { sessionStorage.removeItem('indikin_is_ambassador'); } catch { /* noop */ }
        }
      });
    };

    initAuthListener().catch((err) => {
      console.error('[UserProvider] Auth initialization failed:', err);
      setUser(null);
      setLoading(false);
      setIsAuthReady(true);
      setIsClaimsReady(true);
      setPassStatus('inactive');
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [setGuestEmail]);

  // --- NEW: Dedicated effect for fetching pass status for non-privileged users ---
  useEffect(() => {
    const checkViewerPassStatus = async () => {
      // Only run for viewers after authentication is fully resolved.
      if (isClaimsReady && user && (user.role === 'viewer' || user.role === 'filmmaker-basic')) {
        try {
          const idToken = await user.getIdToken();
          const response = await getUserPassStatus(idToken);
          setPassStatus(response.hasPass ? 'active' : 'inactive');
        } catch (error) {
          console.error("Failed to fetch user pass status:", error);
          setPassStatus('inactive'); // Default to inactive on error.
        }
      }
    };
    checkViewerPassStatus();
  }, [isClaimsReady, user]);


  const signInUser = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const auth = await getLazyAuth();
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const signUpUser = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const auth = await getLazyAuth();
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const signOutUser = useCallback(async () => {
    setLoading(true);
    try {
      const auth = await getLazyAuth();
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out: ", error);
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    setLoading(true);
    try {
      const auth = await getLazyAuth();
      const { sendPasswordResetEmail } = await import('firebase/auth');
      const actionCodeSettings = {
        url: (import.meta.env.VITE_REACT_APP_URL || window.location.origin) + '/auth/action',
      };
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      const auth = await getLazyAuth();
      const { GoogleAuthProvider, signInWithPopup, signInWithRedirect } = await import('firebase/auth');
      const provider = new GoogleAuthProvider();
      try {
        await signInWithPopup(auth, provider);
      } catch (error) {
        if (error.code === 'auth/popup-blocked' || error.code === 'auth/cancelled-popup-request') {
          console.warn('Popup was blocked or cancelled, falling back to redirect method.');
          try {
            await signInWithRedirect(auth, provider);
          } catch (redirectError) {
            console.error("Error with Google sign-in redirect: ", redirectError);
            setLoading(false);
            throw redirectError;
          }
        } else {
          console.error("Error signing in with Google: ", error);
          setLoading(false);
          throw error;
        }
      }
    } catch (err) {
      setLoading(false);
      console.error("Failed to load auth module", err);
      // CRITICAL FIX: Propagate the error so the UI (SocialLoginButtons) knows it failed.
      throw err;
    }
  }, []);

  const signInWithDiscord = useCallback(async () => {
    setLoading(true);
    try {
      const auth = await getLazyAuth();
      const { OAuthProvider, signInWithPopup, signInWithRedirect } = await import('firebase/auth');
      const provider = new OAuthProvider('discord.com');
      try {
        await signInWithPopup(auth, provider);
      } catch (error) {
        if (error.code === 'auth/popup-blocked' || error.code === 'auth/cancelled-popup-request') {
          console.warn('Popup was blocked or cancelled, falling back to redirect method.');
          try {
            await signInWithRedirect(auth, provider);
          } catch (redirectError) {
            console.error("Error with Discord sign-in redirect: ", redirectError);
            setLoading(false);
            throw redirectError;
          }
        } else {
          console.error("Error signing in with Discord: ", error);
          setLoading(false);
          throw error;
        }
      }
    } catch (err) {
      setLoading(false);
      console.error("Failed to load auth module", err);
    }
  }, []);

  const refreshPassStatus = useCallback(async () => {
    const auth = await getLazyAuth();
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      try {
        const idToken = await firebaseUser.getIdToken(true);
        const response = await getUserPassStatus(idToken);
        setPassStatus(response.hasPass ? 'active' : 'inactive');
      } catch (error) {
        console.error("Failed to refresh user pass status:", error);
        setPassStatus('inactive');
      }
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const auth = await getLazyAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;

    try {
      const idTokenResult = await firebaseUser.getIdTokenResult(true);
      const userRole = idTokenResult.claims.role || 'viewer';

      // --- LAZY LOAD FIRESTORE ---
      const db = await getDb();
      const { doc, getDoc } = await import('firebase/firestore');
      // ---------------------------

      const userRef = doc(db, 'users', firebaseUser.uid);
      const userSnap = await getDoc(userRef);

      const userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        providerData: firebaseUser.providerData,
        role: userRole,
        getIdToken: (forceRefresh) => firebaseUser.getIdToken(forceRefresh),
      };

      if (userSnap.exists()) {
        setUser({ ...userData, ...userSnap.data(), role: userRole });
      } else {
        setUser(userData);
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  }, []);

  const deleteUserAccount = useCallback(async (currentPassword) => {
    const auth = await getLazyAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) throw new Error("No user is signed in.");

    const hasPassword = firebaseUser.providerData.some(p => p.providerId === 'password');

    try {
      const { reauthenticateWithCredential, EmailAuthProvider, deleteUser } = await import('firebase/auth');

      if (hasPassword) {
        if (!currentPassword) {
          throw new Error(t('settings.dangerZone.reauthError'));
        }
        const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
        await reauthenticateWithCredential(firebaseUser, credential);
      }

      const idToken = await firebaseUser.getIdToken();
      await apiDeleteUserAccount(idToken);

      await deleteUser(firebaseUser);

    } catch (error) {
      console.error("Error deleting account:", error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        throw new Error(t('settings.errors.auth/wrong-password'));
      }
      throw error;
    }
  }, [t]);

  const value = useMemo(() => ({
    user,
    auth: authInstance,
    loading,
    isAuthReady,
    isClaimsReady,
    passStatus,
    signInUser,
    signUpUser,
    signInWithGoogle,
    signInWithDiscord,
    signOutUser,
    resetPassword,
    refreshPassStatus,
    refreshUser,
    deleteUserAccount,
  }), [user, authInstance, loading, isAuthReady, isClaimsReady, passStatus, signInUser, signUpUser, signInWithGoogle, signInWithDiscord, signOutUser, resetPassword, refreshPassStatus, refreshUser, deleteUserAccount]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
